import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaFileAudio, FaUpload, FaPlay, FaTrash, FaFileContract, FaPen, FaCheck, FaTimes, FaBan, FaVideo, FaMicrophone, FaPlus } from "react-icons/fa";
import StatusBadge from "../../../components/StatusBadge";
import AdminModal from "../../../components/AdminModal";
import FormField from "../../../components/FormField";
import { useToast } from "../../../components/Toast";
import {
  colors,
  spacing,
  typography,
  radius,
  cardBase,
  inputBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
  transitions,
} from "../../../utils/designTokens";
import {
  actaAdmin,
  cancelarMinuta,
  cancelarProcesamiento,
  cierreSalaMs,
  codigoDeError,
  descartarSesionZoom,
  detallesDeError,
  diaMesMontevideo,
  esProcesamientoEnCurso,
  esSalaGrabando,
  esSalaZoomAbierta,
  esWorkerApagado,
  fechaMontevideo,
  horaMontevideo,
  incluirSesionZoom,
  mensajeDeError,
  subirAUrlFirmada,
} from "../../../utils/asambleas/api";
import {
  ACCEPT_AUDIO,
  AVISO_WORKER_APAGADO,
  ESTADOS_MINUTA,
  ESTADOS_PROCESAMIENTO,
  LIMITES_AUDIO,
  actaDeAsamblea,
  audiosOrdenados,
  contentTypeDeArchivo,
  enColaLocal,
  esAudioDeZoom,
  estadoActa,
  etapasDe,
  etiquetaProcesamiento,
  fechaHora,
  formatoBytes,
  grabacionesZoomSinImportar,
  minutasParaCancelar,
  normalizarProcesamiento,
  procesamientoEnCurso,
  puedeCancelarProcesamiento,
  sesionesZoomPorConfirmar,
  workerApagado,
} from "../../../components/actas/actaFlujo";

// Sección "Acta" del detalle de una asamblea (Fase 2): subir el audio (uno o
// varios archivos, con progreso), pedir que se procese, seguir el estado
// (polling cada 10 s mientras procesa) y abrir el acta en el editor.
// Los archivos van directo al bucket por URL firmada (PUT); nunca pasan por
// Firestore ni quedan públicos.
//
// Con el procesamiento automático apagado para el edificio (workerHabilitado
// false en el servidor) el pedido queda en cola sin avanzar: se avisa, se
// consulta cada minuto y, pasados unos minutos, se deja de consultar hasta
// que se pida. Mientras tanto se ofrece "Cancelar procesamiento" (con
// confirmación): sacarlo de la cola destraba el acta. Lo mismo con las
// minutas de audio que quedaron en cola (cancelarMinuta). Si el servidor
// rechaza volver a procesar un acta existente con el worker apagado (motivo
// "worker_apagado") se explica qué hacer. En modo local el lanzamiento queda
// "local" (lo toma el demonio de scripts/local) y se muestra "En cola (local)".
//
// Con Zoom (procesamiento.fuente "zoom", Fase 3): la grabación la trae el
// worker (etapas "Buscar la reunión de Zoom", "Traer la grabación" e
// "Identificar voces") cuando se cierra la sala: antes no, porque la reunión
// se puede volver a abrir (una 2ª convocatoria, una prueba previa). Si Zoom
// avisó la grabación con la sala abierta (zoom.esperaCierreSala del servidor:
// la encola el cron al cierre) se dice que espera el cierre y no se ofrece
// procesarla (el servidor respondería "sala_abierta", que también se explica).
// Con la sala ya cerrada la espera puede seguir hasta que Zoom avise la
// grabación de cada sesión (como tarde, 12 h): el servidor manda
// zoom.salaCerradaEnMs (el cliente no sabe cuándo cerró: el cron puede esperar
// hasta 2 h por gente conectada), se dice que la sala ya cerró y se ofrece
// "Procesar sin esperar", con confirmación (la sesión que Zoom todavía procesa
// puede quedar afuera del acta). Mientras espera se vuelve a consultar al
// cierre y, ya cerrada, cada minuto, hasta MAX_CONSULTAS_ESPERA ("Volver a
// consultar").
// Después, si quedó una grabación sin importar (no se pudo encolar sola o la
// importación falló) se avisa y se ofrece Procesar o Reintentar aunque no haya
// audios subidos. Las sesiones que
// Zoom grabó fuera del horario de la asamblea no se suman solas: se listan con
// "Incluir sesión" (incluirSesionZoom) y "Descartar" (descartarSesionZoom: la
// sesión queda afuera y el acta se arma con las otras; las descartadas ya no
// se listan).
//
// Modo Sala (Fase 4): cada grabación de la sala entra a los audios como una
// entrada { tipo: "sala", sesionId, sesion, partes } (sin path). Se lista como
// "Modo Sala · sesión N · X partes" y se puede quitar antes de procesar
// (quitarAudio con { aid, sesionId }): por ejemplo, si se usa la grabadora de
// respaldo en su lugar (si no, el acta tendría el audio repetido). No cuentan
// para el tope de archivos subidos a mano (tienen su propio tope en el servidor).
// Si al procesar una grabación de la sala sigue abierta (grabando o subiendo lo
// último), el servidor pide confirmar ("sala_grabando" → confirmarSalaGrabando)
// antes que la de reemplazar el acta (confirmarReproceso).
//
// Props:
//   asamblea       vista de la asamblea ({ id, buildingid, estado, inicioMs, video, ... })
//   onAbrirEditor  (actaId | null) => void   null = redactar a mano un acta nueva

const POLL_MS = 10000;
const POLL_LENTO_MS = 60000;
const MAX_CONSULTAS_LENTAS = 10;
// Esperando la grabación de Zoom con la sala cerrada: una consulta por minuto
// durante media hora (Zoom puede tardar horas; después, "Volver a consultar").
const MAX_CONSULTAS_ESPERA = 30;
const SESIONES = [1, 2, 3, 4, 5, 6];

// procesar con un acta existente y el worker apagado para el edificio: la IA
// reemplazaría el acta y el pedido quedaría trabándola sin avanzar.
const AVISO_REPROCESO_SIN_WORKER =
  "No se puede volver a procesar el audio: el procesamiento automático no está activo para este edificio y la IA reemplazaría el acta que ya existe. Seguí con el acta en el editor o pedile a Habitia que active el procesamiento.";

/** ¿Hay un acta que la IA reemplazaría si se procesa (borrador a mano o en revisión)? */
const actaReemplazable = (acta) => !!acta && (acta.status === "borrador" || acta.status === "en_revision");

/** ¿Es la grabación de una sesión de Modo Sala? (sin path: se quita por sesionId) */
const esGrabacionDeSala = (audio) => !!audio && audio.tipo === "sala" && typeof audio.sesionId === "string" && !!audio.sesionId;

/** "Modo Sala · sesión 2 · 45 partes" (la cantidad de partes de ~1 minuto que llegaron). */
function rotuloSala(audio) {
  const partes = Number(audio.partes);
  const n = Number.isFinite(partes) && partes >= 0 ? Math.floor(partes) : null;
  return `Modo Sala · sesión ${audio.sesion || 1}${n == null ? "" : ` · ${n} ${n === 1 ? "parte" : "partes"}`}`;
}

/**
 * Sesiones de Zoom que el staff descartó (descartarSesionZoom deja la
 * instancia con descartadaEnMs): no se vuelven a ofrecer.
 */
function uuidsDescartados(p) {
  const z = p && p.zoom && typeof p.zoom === "object" ? p.zoom : null;
  const lista = z && Array.isArray(z.instancias) ? z.instancias : [];
  return new Set(
    lista.filter((i) => i && typeof i === "object" && (Number(i.descartadaEnMs) > 0 || i.descartada === true)).map((i) => i.uuid)
  );
}

/** "a partir de las 22:00", o "a partir del 21/10 a las 22:00" si no es hoy (Montevideo). */
function aPartirDe(ms, ahoraMs) {
  const hora = horaMontevideo(ms);
  return fechaMontevideo(ms) === fechaMontevideo(ahoraMs) ? `a partir de las ${hora}` : `a partir del ${diaMesMontevideo(ms)} a las ${hora}`;
}

/**
 * Zoom avisó la grabación con la sala abierta (zoom.esperaCierreSala): el cron
 * la encola al cerrarse, salvo que ya haya un acta (ahí decide el staff, como
 * con cualquier grabación que llega con un acta hecha). Con la sala ya cerrada
 * (`cerrada`: zoom.salaCerradaEnMs) se espera que Zoom termine de procesar la
 * grabación de la última sesión (como tarde, 12 h después del cierre).
 */
function textoEsperaCierre(hayActa, cierreSala, cerrada = false) {
  if (cerrada) {
    return hayActa
      ? "La sala de Zoom ya cerró. Como ya hay un acta, la grabación no se procesa sola: cuando Zoom termine de procesar la de la última sesión (como tarde, 12 horas después del cierre) la podés procesar desde acá."
      : "La sala de Zoom ya cerró: la grabación se trae sola apenas Zoom termine de procesar la de la última sesión (como tarde, 12 horas después del cierre) y la IA redacta el acta. No hace falta procesarla.";
  }
  const cuando = cierreSala != null ? `, ${aPartirDe(cierreSala, Date.now())},` : "";
  return hayActa
    ? `Zoom ya avisó la grabación de la asamblea. Como ya hay un acta, no se procesa sola: cuando se cierre la sala${cuando} la podés procesar desde acá.`
    : `Zoom ya avisó la grabación de la asamblea: se trae sola cuando se cierre la sala${cuando} y la IA redacta el acta. No hace falta procesarla.`;
}

// El acta de la asamblea en la lista del edificio (por asambleaId / asm_{aid}
// o por el actaId que dejó el worker en el procesamiento).
function buscarActa(lista, aid, proc) {
  const actas = (lista && lista.actas) || [];
  return actaDeAsamblea(actas, aid) || (proc && proc.actaId ? actas.find((x) => x && x.id === proc.actaId) || null : null);
}

export default function AsambleaActaSeccion({ asamblea, onAbrirEditor }) {
  const aid = asamblea.id;
  const buildingId = asamblea.buildingid;
  const { showToast } = useToast();
  const [proc, setProc] = useState(null);
  const [acta, setActa] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [sesion, setSesion] = useState(1);
  const [subidas, setSubidas] = useState([]); // [{ clave, nombre, bytes, pct, estado, error }]
  const [subiendo, setSubiendo] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  // { tipo: "reproceso" | "quitar" | "cancelar" | "cancelarMinuta" | "incluir" | "descartar" | "sinEsperar" | "salaGrabando",
  //   audio?, minuta?, sesion?, reproceso?, salaGrabando?, grabando?, dispositivos? }
  const [confirmar, setConfirmar] = useState(null);
  const [consultasLentas, setConsultasLentas] = useState(0); // consultas con el worker apagado
  const [consultasEspera, setConsultasEspera] = useState(0); // consultas esperando la grabación de Zoom
  const [avisoSinWorker, setAvisoSinWorker] = useState(false); // procesar respondió "worker_apagado"
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const cierreSala = cierreSalaMs(asamblea);
  const actaPublicada = !!acta && acta.status === "publicada";

  const cargar = useCallback(async () => {
    try {
      const [estado, lista] = await Promise.all([
        actaAdmin("estadoProcesamiento", { aid }).catch((e) => {
          // Sin documento de procesamiento todavía = no se subió nada.
          if (codigoDeError(e) === "not-found") return null;
          throw e;
        }),
        actaAdmin("listar", { buildingId }),
      ]);
      const p = normalizarProcesamiento(estado);
      setProc(p);
      setActa(buscarActa(lista, aid, p));
      setErrorCarga("");
    } catch (e) {
      setErrorCarga(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }, [aid, buildingId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Polling mientras el worker procesa (cada 10 s, sin superponer llamadas).
  // Con el worker apagado: cada minuto y, después de MAX_CONSULTAS_LENTAS, se corta.
  useEffect(() => {
    if (!procesamientoEnCurso(proc)) return undefined;
    const lento = workerApagado(proc);
    if (lento && consultasLentas >= MAX_CONSULTAS_LENTAS) return undefined;
    let cancelado = false;
    const t = setTimeout(async () => {
      try {
        const p = normalizarProcesamiento(await actaAdmin("estadoProcesamiento", { aid }));
        if (cancelado) return;
        setProc(p);
        setConsultasLentas((n) => (workerApagado(p) ? n + 1 : 0));
        if (p.estado === "listo") {
          const lista = await actaAdmin("listar", { buildingId });
          if (!cancelado) setActa(buscarActa(lista, aid, p));
          showToast("La IA terminó el acta: revisala en el editor antes de publicarla.", "success");
        } else if (p.estado === "error") {
          showToast("No se pudo procesar el audio de la asamblea.", "error");
        }
      } catch (_) {
        // Error de red puntual: se reintenta en el próximo ciclo.
        if (!cancelado) {
          setProc((x) => (x ? { ...x } : x));
          if (lento) setConsultasLentas((n) => n + 1);
        }
      }
    }, lento ? POLL_LENTO_MS : POLL_MS);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [proc, consultasLentas, aid, buildingId, showToast]);

  // Mientras la grabación de Zoom espera (zoom.esperaCierreSala): se vuelve a
  // consultar al cierre de la sala y, ya cerrada, cada minuto (cuando Zoom
  // avisa la grabación de la última sesión el servidor la encola sola o, con
  // un acta, aparece "Procesar"), hasta MAX_CONSULTAS_ESPERA. Sin superponer
  // llamadas; un error de red cuenta como consulta.
  useEffect(() => {
    const z = proc && proc.zoom;
    if (!z || z.esperaCierreSala !== true || procesamientoEnCurso(proc) || actaPublicada) return undefined;
    if (consultasEspera >= MAX_CONSULTAS_ESPERA) return undefined;
    const ahora = Date.now();
    const cerrada = Number(z.salaCerradaEnMs) > 0 || (cierreSala != null && ahora >= cierreSala);
    if (!cerrada && cierreSala == null) return undefined;
    let cancelado = false;
    const t = setTimeout(async () => {
      try {
        const p = normalizarProcesamiento(await actaAdmin("estadoProcesamiento", { aid }));
        if (!cancelado) setProc(p);
      } catch (_) {
        // Error de red puntual: se reintenta en la próxima consulta.
      }
      if (!cancelado) setConsultasEspera((n) => n + 1);
    }, cerrada ? POLL_LENTO_MS : cierreSala - ahora + POLL_LENTO_MS);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [proc, consultasEspera, cierreSala, actaPublicada, aid]);

  const volverAConsultar = () => {
    setConsultasLentas(0);
    cargar();
  };

  const volverAConsultarZoom = () => {
    setConsultasEspera(0);
    cargar();
  };

  // Aviso del navegador si se cierra la pestaña con subidas en curso.
  useEffect(() => {
    if (!subiendo) return undefined;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [subiendo]);

  // Al salir del detalle se cancelan las subidas en curso.
  useEffect(() => () => abortRef.current && abortRef.current.abort(), []);

  const marcar = (clave, patch) => setSubidas((s) => s.map((x) => (x.clave === clave ? { ...x, ...patch } : x)));

  const subirArchivos = async (fileList) => {
    const archivos = Array.from(fileList || []).sort((a, b) => a.name.localeCompare(b.name, "es", { numeric: true }));
    if (!archivos.length) return;
    // Las grabaciones de Modo Sala tienen su propio tope: acá cuentan solo los archivos.
    const yaHay = ((proc && proc.audios) || []).filter((x) => !x || x.tipo !== "sala").length;
    if (yaHay + archivos.length > LIMITES_AUDIO.archivosPorAsamblea) {
      showToast(`Se pueden subir hasta ${LIMITES_AUDIO.archivosPorAsamblea} archivos por asamblea.`, "warning");
      return;
    }
    const lote = archivos.map((f, i) => {
      const contentType = contentTypeDeArchivo(f);
      let error = "";
      if (!contentType) error = "Formato no admitido (usá mp3, m4a, wav, ogg, opus, webm, aac o mp4).";
      else if (!f.size) error = "El archivo está vacío.";
      else if (f.size > LIMITES_AUDIO.bytesPorArchivo) error = `Pasa de ${formatoBytes(LIMITES_AUDIO.bytesPorArchivo)}.`;
      return { clave: `${Date.now()}-${i}-${f.name}`, archivo: f, nombre: f.name, bytes: f.size, contentType, pct: 0, estado: error ? "error" : "en_cola", error };
    });
    setSubidas((s) => [...s.filter((x) => x.estado !== "lista"), ...lote.map(({ archivo, contentType, ...resto }) => resto)]);
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    abortRef.current = ctrl;
    setSubiendo(true);
    let ok = 0;
    try {
      for (const it of lote) {
        if (it.estado === "error") continue;
        if (ctrl && ctrl.signal.aborted) {
          marcar(it.clave, { estado: "error", error: "Cancelada." });
          continue;
        }
        marcar(it.clave, { estado: "subiendo" });
        try {
          const sub = await actaAdmin("subirAudio", { aid, nombre: it.nombre, contentType: it.contentType, bytes: it.bytes, sesion });
          if (!sub || !sub.uploadUrl || !sub.path) throw new Error("El servidor no devolvió dónde subir el audio.");
          await subirAUrlFirmada(sub.uploadUrl, it.archivo, {
            contentType: it.contentType,
            headers: sub.headers || {},
            signal: ctrl ? ctrl.signal : undefined,
            onProgreso: (pct) => marcar(it.clave, { pct }),
          });
          marcar(it.clave, { estado: "registrando", pct: 100 });
          const reg = await actaAdmin("registrarAudio", { aid, path: sub.path });
          if (reg && Array.isArray(reg.audios)) {
            setProc((p) => ({ ...(p || normalizarProcesamiento(null)), audios: reg.audios }));
          }
          marcar(it.clave, { estado: "lista" });
          ok += 1;
        } catch (e) {
          marcar(it.clave, { estado: "error", error: e && e.name === "AbortError" ? "Cancelada." : mensajeDeError(e) });
        }
      }
    } finally {
      setSubiendo(false);
      abortRef.current = null;
      if (ok) showToast(ok === 1 ? "Audio subido." : `${ok} audios subidos.`, "success");
    }
  };

  // Procesar puede pedir dos confirmaciones, en este orden: una grabación de
  // Modo Sala que sigue abierta ("sala_grabando" → confirmarSalaGrabando) y un
  // acta que la IA reemplazaría (→ confirmarReproceso). Lo ya confirmado viaja
  // en el pedido siguiente.
  const procesar = async (confirmarReproceso = false, { confirmarSalaGrabando = false } = {}) => {
    setTrabajando(true);
    setAvisoSinWorker(false);
    try {
      const res =
        (await actaAdmin("procesar", {
          aid,
          ...(confirmarReproceso ? { confirmarReproceso: true } : {}),
          ...(confirmarSalaGrabando ? { confirmarSalaGrabando: true } : {}),
        })) || {};
      if (res.requiereConfirmacion && res.motivo === "sala_grabando") {
        setConfirmar({
          tipo: "salaGrabando",
          reproceso: confirmarReproceso,
          grabando: Number(res.grabando) || 0,
          dispositivos: Array.isArray(res.dispositivos) ? res.dispositivos.filter((d) => typeof d === "string" && d) : [],
        });
        return;
      }
      if (res.requiereConfirmacion) {
        setConfirmar({ tipo: "reproceso", salaGrabando: confirmarSalaGrabando });
        return;
      }
      const habilitado = typeof res.workerHabilitado === "boolean" ? res.workerHabilitado : null;
      setProc((p) => ({
        ...(p || normalizarProcesamiento(null)),
        estado: res.estado || "pendiente",
        etapa: null,
        ultimoError: null,
        lanzamiento: null,
        workerHabilitado: habilitado,
      }));
      setConsultasLentas(0);
      if (habilitado === false) showToast(`El audio quedó en cola. ${AVISO_WORKER_APAGADO}`, "warning");
      else showToast("Listo: el audio quedó en cola para procesar.", "success");
    } catch (e) {
      const motivo = String(detallesDeError(e).motivo || "");
      if (esWorkerApagado(e)) {
        setAvisoSinWorker(true);
        showToast(AVISO_REPROCESO_SIN_WORKER, "warning");
      } else if (esSalaZoomAbierta(e)) {
        // La sala de Zoom sigue abierta: la grabación la encola el cron al cierre. Se recarga para mostrarlo.
        showToast(mensajeDeError(e), "warning");
        await cargar();
      } else if (esSalaGrabando(e)) {
        // Todavía no hay audio y Modo Sala sigue grabando: hay que terminarla primero.
        showToast(mensajeDeError(e), "warning");
      } else if (!confirmarReproceso && codigoDeError(e) === "failed-precondition" && /reproce|confirm/i.test(motivo)) {
        setConfirmar({ tipo: "reproceso", salaGrabando: confirmarSalaGrabando });
      } else {
        showToast(mensajeDeError(e), "error");
      }
    } finally {
      setTrabajando(false);
    }
  };

  // Saca de la cola lo que no va a correr (worker apagado). El servidor lo
  // rechaza si el worker ya lo tomó (motivo "en_curso").
  const cancelar = async () => {
    setTrabajando(true);
    try {
      await cancelarProcesamiento(aid);
      showToast("Procesamiento cancelado: el audio salió de la cola.", "success");
    } catch (e) {
      const enCurso = esProcesamientoEnCurso(e);
      showToast(enCurso ? "El procesamiento ya arrancó y no se puede cancelar: esperá a que termine." : mensajeDeError(e), enCurso ? "warning" : "error");
    } finally {
      setTrabajando(false);
      setConsultasLentas(0);
      await cargar();
    }
  };

  // Saca de la cola una minuta de audio que no va a correr (worker apagado). El
  // servidor lo rechaza si el worker ya la tomó (motivo "en_curso").
  const cancelarLaMinuta = async (m) => {
    const actaId = m.actaId || (acta && acta.id);
    if (!actaId) return;
    setTrabajando(true);
    try {
      await cancelarMinuta(actaId, m.minutaId);
      showToast("Minuta cancelada: salió de la cola y no se aplica al acta.", "success");
    } catch (e) {
      const enCurso = esProcesamientoEnCurso(e);
      showToast(enCurso ? "La minuta ya se está aplicando y no se puede cancelar: esperá a que termine." : mensajeDeError(e), enCurso ? "warning" : "error");
    } finally {
      setTrabajando(false);
      await cargar();
    }
  };

  // Suma al acta una sesión que Zoom grabó fuera del horario de la asamblea. El
  // servidor la deja en cola como "procesar": con un acta existente pide
  // confirmar que la IA la vuelva a redactar (confirmarReproceso).
  const incluirSesion = async (sesionZoom, confirmarReproceso = false) => {
    setTrabajando(true);
    setAvisoSinWorker(false);
    try {
      const res = (await incluirSesionZoom(aid, sesionZoom.uuid, { confirmarReproceso })) || {};
      if (res.requiereConfirmacion) {
        setConfirmar({ tipo: "incluir", sesion: sesionZoom, reproceso: true });
        return;
      }
      if (res.yaIncluida) showToast("Esa sesión ya estaba incluida.", "success");
      else if (res.estado === "pendiente" && res.workerHabilitado === false) {
        showToast(`Sesión incluida: la grabación quedó en cola. ${AVISO_WORKER_APAGADO}`, "warning");
      } else if (res.estado === "pendiente") showToast("Sesión incluida: la grabación quedó en cola para procesar.", "success");
      else showToast("Sesión incluida: se suma al procesamiento que ya está en cola.", "success");
    } catch (e) {
      if (esWorkerApagado(e)) {
        setAvisoSinWorker(true);
        showToast(AVISO_REPROCESO_SIN_WORKER, "warning");
      } else {
        showToast(mensajeDeError(e), esProcesamientoEnCurso(e) || esSalaZoomAbierta(e) ? "warning" : "error");
      }
    } finally {
      setTrabajando(false);
      setConsultasLentas(0);
      await cargar();
    }
  };

  // Deja afuera del acta una sesión que Zoom grabó fuera del horario (una
  // prueba, alguien que volvió a abrir la sala cuando ya había terminado): el
  // worker ya no la espera y el acta se arma con las otras sesiones. Si el
  // servidor pide confirmar que la IA vuelva a redactar un acta existente
  // (requiereConfirmacion), se pregunta y se manda confirmarReproceso.
  const descartarSesion = async (sesionZoom, confirmarReproceso = false) => {
    setTrabajando(true);
    setAvisoSinWorker(false);
    try {
      const res = (await descartarSesionZoom(aid, sesionZoom.uuid, { confirmarReproceso })) || {};
      if (res.requiereConfirmacion) {
        setConfirmar({ tipo: "descartar", sesion: sesionZoom, reproceso: true });
        return;
      }
      if (res.yaDescartada) showToast("Esa sesión ya estaba descartada.", "success");
      else if (res.estado === "pendiente" && res.workerHabilitado === false) {
        showToast(`Sesión descartada: el acta se arma con las otras sesiones y quedó en cola. ${AVISO_WORKER_APAGADO}`, "warning");
      } else if (res.estado === "pendiente") showToast("Sesión descartada: el acta se arma con las otras sesiones.", "success");
      else showToast("Sesión descartada: no entra al acta.", "success");
    } catch (e) {
      if (esWorkerApagado(e)) {
        setAvisoSinWorker(true);
        showToast(AVISO_REPROCESO_SIN_WORKER, "warning");
      } else {
        showToast(mensajeDeError(e), esProcesamientoEnCurso(e) || esSalaZoomAbierta(e) ? "warning" : "error");
      }
    } finally {
      setTrabajando(false);
      setConsultasLentas(0);
      await cargar();
    }
  };

  const pedirProcesar = () => {
    // Si ya hay acta (borrador a mano o en revisión) se avisa antes: la IA la reescribe.
    if (actaReemplazable(acta)) setConfirmar({ tipo: "reproceso" });
    else procesar(false);
  };

  // El botón de confirmación del modal. Incluir una sesión con un acta ya
  // redactada la vuelve a generar: el modal ya lo avisó, así que va confirmado.
  // Descartar va confirmado solo si el servidor lo pidió (el modal lo avisó).
  // Procesar sin esperar a Zoom: el modal ya avisó que la IA reemplaza el acta
  // si hay una, así que va confirmado en ese caso.
  const confirmarAccion = () => {
    const c = confirmar;
    setConfirmar(null);
    if (!c) return;
    if (c.tipo === "cancelar") cancelar();
    else if (c.tipo === "quitar") quitarAudio(c.audio);
    else if (c.tipo === "cancelarMinuta") cancelarLaMinuta(c.minuta);
    else if (c.tipo === "incluir") incluirSesion(c.sesion, c.reproceso === true || actaReemplazable(acta));
    else if (c.tipo === "descartar") descartarSesion(c.sesion, c.reproceso === true);
    else if (c.tipo === "sinEsperar") procesar(actaReemplazable(acta));
    else if (c.tipo === "salaGrabando") procesar(c.reproceso === true, { confirmarSalaGrabando: true });
    else procesar(true, { confirmarSalaGrabando: c.salaGrabando === true });
  };

  // Un archivo subido (o traído de Zoom) se quita por su path; una grabación de
  // Modo Sala, por su sesionId (no tiene path: son muchas partes).
  const quitarAudio = async (audio) => {
    const deSala = esGrabacionDeSala(audio);
    const mismo = (x) => (deSala ? !!x && x.tipo === "sala" && x.sesionId === audio.sesionId : !!x && x.path === audio.path);
    setTrabajando(true);
    try {
      const res = await actaAdmin("quitarAudio", deSala ? { aid, sesionId: audio.sesionId } : { aid, path: audio.path });
      setProc((p) => {
        const base = p || normalizarProcesamiento(null);
        const audios = res && Array.isArray(res.audios) ? res.audios : base.audios.filter((x) => !mismo(x));
        return { ...base, audios };
      });
      showToast(deSala ? "Grabación de la sala quitada." : "Audio quitado.", "success");
    } catch (e) {
      showToast(mensajeDeError(e), "error");
    } finally {
      setTrabajando(false);
    }
  };

  // ── Render ──
  const titulo = (
    <h2 style={S.h2}>
      <FaFileContract size={14} color={colors.primary} aria-hidden="true" /> Acta
    </h2>
  );

  if (cargando) {
    return (
      <div style={cardBase}>
        {titulo}
        <p style={S.hint}>Cargando…</p>
      </div>
    );
  }
  if (errorCarga) {
    return (
      <div style={cardBase}>
        {titulo}
        <p style={{ ...S.texto, color: colors.errorDark }}>No pudimos cargar el estado del acta: {errorCarga}</p>
        <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => { setCargando(true); cargar(); }}>
          Reintentar
        </button>
      </div>
    );
  }

  const p = proc || normalizarProcesamiento(null);
  const audios = audiosOrdenados(p.audios);
  const hayGrabacionDeSala = audios.some(esGrabacionDeSala);
  const enCurso = procesamientoEnCurso(p);
  const apagado = workerApagado(p);
  const consultaCortada = apagado && consultasLentas >= MAX_CONSULTAS_LENTAS;
  const publicada = actaPublicada;
  const conZoom =p.fuente === "zoom" || (!!asamblea.video && asamblea.video.proveedor === "zoom");
  // Grabación de Zoom sin importar: se procesa aunque no haya audios subidos (el worker la trae).
  const grabacionesPendientes = publicada ? 0 : grabacionesZoomSinImportar(p);
  const zoomSinImportar = grabacionesPendientes > 0;
  // Zoom avisó la grabación con la sala todavía abierta: el cron la encola
  // cuando se cierra (zoom.esperaCierreSala del servidor; antes no, porque la
  // reunión se puede volver a abrir). Mientras tanto no se ofrece procesarla
  // (el servidor respondería "sala_abierta"); subir el audio a mano sí.
  const esperaCierre = !publicada && !enCurso && !!p.zoom && p.zoom.esperaCierreSala === true;
  // Ya cerró (lo dice el servidor) y Zoom todavía procesa la grabación de alguna
  // sesión: se puede procesar sin esperar, con confirmación.
  const salaCerrada = esperaCierre && Number(p.zoom.salaCerradaEnMs) > 0;
  const puedeSubir = !enCurso && !publicada;
  const puedeQuitar = ["sin_audio", "error", "cancelado"].includes(p.estado) && !publicada && !subiendo;
  const puedeProcesar = (audios.length > 0 || p.fuente === "zoom") && !esperaCierre && !enCurso && !publicada && !subiendo;
  const puedeProcesarSinEsperar = salaCerrada && !subiendo;
  const consultaEsperaCortada = esperaCierre && consultasEspera >= MAX_CONSULTAS_ESPERA;
  const puedeCancelar = enCurso && puedeCancelarProcesamiento(p) && !publicada;
  // Las que el staff ya descartó no se vuelven a ofrecer.
  const descartadas = uuidsDescartados(p);
  const sesionesFuera = publicada ? [] : sesionesZoomPorConfirmar(p).filter((s) => !descartadas.has(s.uuid));
  const minutasEnCola = publicada ? [] : minutasParaCancelar(p);
  const estProc = ESTADOS_PROCESAMIENTO[p.estado] || ESTADOS_PROCESAMIENTO.sin_audio;
  const futura = Number(asamblea.inicioMs) > Date.now();
  const enVuelo = subidas.filter((x) => x.estado !== "lista");
  const estActa = acta ? estadoActa(acta.status) : null;
  const sinImportarYSinAudio = zoomSinImportar && p.estado === "sin_audio";
  let etiquetaBadge = etiquetaProcesamiento(p);
  if (esperaCierre) etiquetaBadge = salaCerrada ? "Espera la grabación de Zoom" : "Espera el cierre de la sala";
  else if (sinImportarYSinAudio) etiquetaBadge = "Grabación sin importar";
  else if (audios.length && p.estado === "sin_audio") etiquetaBadge = "Audio subido";
  const textoProcesar =
    p.estado === "error" ? "Reintentar" : acta ? "Volver a procesar" : audios.length ? "Procesar audio" : "Procesar la grabación de Zoom";
  const modal = confirmar ? textoConfirmacion(confirmar, acta) : null;

  return (
    <div style={{ ...cardBase, display: "flex", flexDirection: "column", gap: spacing[4] }}>
      <div style={S.head}>
        {titulo}
        <StatusBadge status={esperaCierre || sinImportarYSinAudio ? "pendiente" : estProc.badge} label={etiquetaBadge} size="sm" />
      </div>

      {/* Acta existente */}
      {acta ? (
        <div style={S.actaFila}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
            <StatusBadge status={estActa.badge} label={estActa.label} size="sm" />
            <span style={S.texto}>
              {acta.actaNumero ? `Acta N° ${acta.actaNumero}` : "Acta sin número"}
              {acta.origen === "ia" ? " · redactada por la IA" : " · redactada a mano"}
              {acta.updatedAtMs ? ` · actualizada el ${fechaHora(acta.updatedAtMs)}` : ""}
            </span>
          </div>
          <button
            type="button"
            style={acta.status === "en_revision" ? btnPrimary : btnSecondary}
            className={acta.status === "en_revision" ? "habitia-btn-primary" : "habitia-btn-secondary"}
            onClick={() => onAbrirEditor(acta.id)}
            disabled={subiendo}
            title={subiendo ? "Esperá a que terminen las subidas" : undefined}
          >
            <FaPen /> {publicada ? "Ver el acta" : "Abrir en el editor"}
          </button>
        </div>
      ) : (
        !enCurso && (
          <p style={S.texto}>
            {conZoom
              ? "Con Zoom, la grabación se trae sola cuando se cierra la sala, después del fin previsto, y la IA redacta el acta con citas del audio para que la revises. Si no llega, subí el audio."
              : "Subí la grabación de la asamblea y la IA redacta el acta con citas del audio para que la revises."}{" "}
            También podés{" "}
            <button type="button" style={S.link} onClick={() => onAbrirEditor(null)} disabled={subiendo}>
              redactarla a mano
            </button>
            .
          </p>
        )
      )}

      {/* Grabación de Zoom que espera el cierre de la sala (la encola el cron al cerrarse, salvo que ya haya un acta)
          o, con la sala ya cerrada, que Zoom termine de procesar la de la última sesión */}
      {esperaCierre && (
        <div style={S.aviso} role="status">
          <FaVideo size={12} aria-hidden="true" /> {textoEsperaCierre(!!acta, cierreSala, salaCerrada)}
        </div>
      )}
      {consultaEsperaCortada && (
        <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" }}>
          <span style={S.hint}>Dejamos de consultar el estado automáticamente.</span>
          <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={volverAConsultarZoom}>
            Volver a consultar
          </button>
        </div>
      )}

      {/* Grabación de Zoom sin importar (no se pudo encolar sola, falló la importación o la reunión se reinició) */}
      {zoomSinImportar && !esperaCierre && (
        <div style={S.aviso} role="status">
          <FaVideo size={12} aria-hidden="true" />{" "}
          {grabacionesPendientes > 1
            ? `Hay ${grabacionesPendientes} grabaciones de Zoom sin importar.`
            : "Hay una grabación de Zoom sin importar."}{" "}
          {grabacionesPendientes > 1
            ? p.estado === "error"
              ? "Reintentá para traerlas y redactar el acta."
              : "Procesalas para traerlas y que la IA redacte el acta."
            : p.estado === "error"
              ? "Reintentá para traerla y redactar el acta."
              : "Procesala para traerla y que la IA redacte el acta."}
        </div>
      )}

      {/* Estado del procesamiento */}
      {enCurso && <Etapas p={p} apagado={apagado} />}
      {puedeCancelar && (
        <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" }}>
          <span style={{ ...S.hint, flex: 1, minWidth: 220 }}>
            Mientras esté en cola, el acta de la asamblea no se puede guardar ni publicar. Si no vas a esperar, sacalo de la
            cola y seguí con el acta a mano.
          </span>
          <button
            type="button"
            style={btnDanger}
            className="habitia-btn-danger"
            onClick={() => setConfirmar({ tipo: "cancelar" })}
            disabled={trabajando}
          >
            <FaBan /> Cancelar procesamiento
          </button>
        </div>
      )}
      {enCurso && consultaCortada && (
        <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" }}>
          <span style={S.hint}>Dejamos de consultar el estado automáticamente.</span>
          <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={volverAConsultar}>
            Volver a consultar
          </button>
        </div>
      )}
      {p.estado === "error" && (
        <div style={S.error} role="alert">
          No se pudo procesar el audio{p.ultimoError ? `: ${p.ultimoError}` : "."}
          {p.intentos ? ` (intentos: ${p.intentos})` : ""}
        </div>
      )}
      {p.estado === "listo" && acta && acta.status === "en_revision" && (
        <p style={S.texto}>La IA terminó el acta. Revisá las marcas y las citas antes de aprobarla y publicarla.</p>
      )}
      {p.estado === "cancelado" && !enCurso && (
        <p style={S.hint}>
          {audios.length
            ? "Cancelaste el procesamiento: los audios siguen subidos y los podés volver a procesar."
            : "Cancelaste el procesamiento: la grabación de Zoom se puede volver a procesar."}
        </p>
      )}
      {avisoSinWorker && !enCurso && (
        <div style={S.aviso} role="alert">
          {AVISO_REPROCESO_SIN_WORKER}
        </div>
      )}

      {/* Sesiones que Zoom grabó fuera del horario: no se suman solas */}
      {sesionesFuera.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
          <span style={S.texto}>Sesiones de Zoom fuera del horario</span>
          <span style={S.hint}>
            Zoom grabó estas sesiones fuera del horario de la asamblea (por ejemplo, alguien volvió a abrir la sala cuando ya
            había terminado). No se suman al acta salvo que las incluyas; si no fueron parte de la asamblea, descartalas.
            {p.estado === "error" ? " Mientras no decidas, el acta no se arma." : ""}
          </span>
          <ul style={S.lista} aria-label="Sesiones de Zoom fuera del horario">
            {sesionesFuera.map((s) => {
              const cuando = s.inicioMs ? `que empezó el ${fechaHora(s.inicioMs)}` : "sin hora de inicio";
              return (
                <li key={s.uuid} style={S.audioFila}>
                  <FaVideo color={colors.gray600} aria-hidden="true" />
                  <span style={{ ...S.texto, flex: 1 }}>
                    {s.inicioMs ? `Empezó el ${fechaHora(s.inicioMs)}` : "Sin hora de inicio"}
                    {s.duracionMin ? ` · ${s.duracionMin} min` : ""}
                    {s.sinGrabacion ? " · sin grabación (solo asistencia)" : ""}
                  </span>
                  <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                    <button
                      type="button"
                      style={btnSecondary}
                      className="habitia-btn-secondary"
                      onClick={() => setConfirmar({ tipo: "incluir", sesion: s })}
                      disabled={trabajando || enCurso}
                      aria-label={`Incluir la sesión ${cuando}`}
                      title={enCurso ? "Esperá a que termine el procesamiento" : undefined}
                    >
                      <FaPlus /> Incluir sesión
                    </button>
                    <button
                      type="button"
                      style={btnDanger}
                      className="habitia-btn-danger"
                      onClick={() => setConfirmar({ tipo: "descartar", sesion: s })}
                      disabled={trabajando || enCurso}
                      aria-label={`Descartar la sesión ${cuando}`}
                      title={enCurso ? "Esperá a que termine el procesamiento" : undefined}
                    >
                      <FaTimes /> Descartar
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Minutas (de audio o escritas) en cola con el worker apagado */}
      {minutasEnCola.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
          <span style={S.texto}>Minutas en cola</span>
          <span style={S.hint}>
            {AVISO_WORKER_APAGADO} Si no vas a esperar, cancelala: el acta queda como está y la seguís a mano.
          </span>
          <ul style={S.lista} aria-label="Minutas en cola">
            {minutasEnCola.map((m) => {
              const cuando = m.creadaEnMs ? ` del ${fechaHora(m.creadaEnMs)}` : "";
              return (
                <li key={m.minutaId} style={S.audioFila}>
                  {m.origen === "texto" ? (
                    <FaPen color={colors.gray600} aria-hidden="true" />
                  ) : (
                    <FaMicrophone color={colors.gray600} aria-hidden="true" />
                  )}
                  <span style={{ ...S.texto, flex: 1 }}>
                    Minuta{m.origen === "texto" ? " escrita" : ""}{cuando} · {ESTADOS_MINUTA[m.estado] || ESTADOS_MINUTA.pendiente}
                  </span>
                  {(m.actaId || (acta && acta.id)) && (
                    <button
                      type="button"
                      style={btnDanger}
                      className="habitia-btn-danger"
                      onClick={() => setConfirmar({ tipo: "cancelarMinuta", minuta: m })}
                      disabled={trabajando}
                      aria-label={`Cancelar la minuta${cuando}`}
                    >
                      <FaBan /> Cancelar
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Audios subidos, traídos de Zoom o grabados en Modo Sala */}
      {audios.length > 0 && (
        <ul style={S.lista} aria-label="Audios de la asamblea">
          {audios.map((au, i) => {
            const deSala = esGrabacionDeSala(au);
            const parte = esAudioDeZoom(au) ? "grabación de Zoom" : `parte ${au.n || i + 1}`;
            const rotulo = deSala ? rotuloSala(au) : `Sesión ${au.sesion || 1} · ${parte}`;
            return (
              <li key={deSala ? `sala-${au.sesionId}` : au.path || `${au.sesion}-${au.n}-${i}`} style={S.audioFila}>
                {deSala ? <FaMicrophone color={colors.gray600} aria-hidden="true" /> : <FaFileAudio color={colors.gray600} aria-hidden="true" />}
                <span style={{ ...S.texto, flex: 1 }}>
                  {rotulo}
                  {au.bytes ? ` · ${formatoBytes(au.bytes)}` : ""}
                  {au.subidoEnMs ? ` · ${fechaHora(au.subidoEnMs)}` : ""}
                </span>
                {puedeQuitar && (au.path || deSala) && (
                  <button
                    type="button"
                    style={btnGhost}
                    className="habitia-btn-ghost"
                    onClick={() => setConfirmar({ tipo: "quitar", audio: au })}
                    disabled={trabajando}
                    aria-label={deSala ? `Quitar la grabación de Modo Sala de la sesión ${au.sesion || 1}` : `Quitar el audio de la sesión ${au.sesion || 1}, ${parte}`}
                    title="Quitar"
                  >
                    <FaTrash />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Subidas en curso o con error */}
      {enVuelo.length > 0 && (
        <ul style={S.lista} aria-label="Subidas">
          {enVuelo.map((it) => (
            <li key={it.clave} style={S.subida}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                <span style={{ ...S.texto, flex: 1, overflowWrap: "anywhere" }}>
                  {it.nombre} · {formatoBytes(it.bytes)}
                </span>
                <span style={S.hint}>
                  {it.estado === "en_cola" && "En espera"}
                  {it.estado === "subiendo" && `${it.pct} %`}
                  {it.estado === "registrando" && "Confirmando…"}
                </span>
                {it.estado === "error" && (
                  <button
                    type="button"
                    style={btnGhost}
                    className="habitia-btn-ghost"
                    onClick={() => setSubidas((s) => s.filter((x) => x.clave !== it.clave))}
                    aria-label={`Quitar ${it.nombre} de la lista`}
                    title="Quitar de la lista"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              {(it.estado === "subiendo" || it.estado === "registrando") && <Barra pct={it.pct} etiqueta={`Subida de ${it.nombre}`} />}
              {it.estado === "error" && <span style={{ ...S.hint, color: colors.errorDark }}>{it.error}</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Acciones */}
      {(puedeSubir || puedeProcesar || puedeProcesarSinEsperar || subiendo) && (
        <div style={S.acciones}>
          {puedeSubir && (
            <>
              <FormField label="Sesión" htmlFor={`asm-acta-sesion-${aid}`} style={{ width: 140 }}>
                <select
                  id={`asm-acta-sesion-${aid}`}
                  className="habitia-input"
                  style={inputBase}
                  value={sesion}
                  onChange={(e) => setSesion(Number(e.target.value))}
                  disabled={subiendo}
                >
                  {SESIONES.map((n) => (
                    <option key={n} value={n}>
                      Sesión {n}
                    </option>
                  ))}
                </select>
              </FormField>
              <input
                ref={inputRef}
                id={`asm-acta-audio-${aid}`}
                type="file"
                multiple
                accept={`audio/*,video/mp4,${ACCEPT_AUDIO}`}
                style={{ display: "none" }}
                aria-label="Elegir archivos de audio de la asamblea"
                onChange={(e) => {
                  const files = e.target.files;
                  subirArchivos(files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                style={btnSecondary}
                className="habitia-btn-secondary"
                onClick={() => inputRef.current && inputRef.current.click()}
                disabled={subiendo || trabajando}
              >
                <FaUpload /> {audios.length ? "Subir otro audio" : "Subir audio"}
              </button>
            </>
          )}
          {subiendo && (
            <button type="button" style={btnDanger} className="habitia-btn-danger" onClick={() => abortRef.current && abortRef.current.abort()}>
              Cancelar subidas
            </button>
          )}
          {puedeProcesar && (
            <button type="button" style={btnPrimary} className="habitia-btn-primary" onClick={pedirProcesar} disabled={trabajando}>
              <FaPlay /> {textoProcesar}
            </button>
          )}
          {puedeProcesarSinEsperar && (
            <button
              type="button"
              style={btnSecondary}
              className="habitia-btn-secondary"
              onClick={() => setConfirmar({ tipo: "sinEsperar" })}
              disabled={trabajando}
            >
              <FaPlay /> Procesar sin esperar
            </button>
          )}
        </div>
      )}
      {puedeSubir && (
        <p style={S.hint}>
          {futura ? "La asamblea todavía no se hizo: subí el audio cuando termine. " : ""}
          Hasta {LIMITES_AUDIO.archivosPorAsamblea} archivos de {formatoBytes(LIMITES_AUDIO.bytesPorArchivo)} (mp3, m4a, wav, ogg,
          opus, webm, aac o mp4). Se unen en orden: por sesión y, dentro de cada una, por nombre de archivo. Usá otra sesión
          si la asamblea pasó a cuarto intermedio y se grabó aparte.
          {hayGrabacionDeSala
            ? " Si subís la grabadora de respaldo en lugar de Modo Sala, quitá las grabaciones de la sala antes de procesar: si no, el audio queda repetido."
            : ""}
        </p>
      )}

      <AdminModal
        open={!!modal}
        onClose={() => setConfirmar(null)}
        title={modal ? modal.titulo : ""}
        size="narrow"
        footer={
          <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%" }}>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => setConfirmar(null)}>
              Volver
            </button>
            {modal && (
              <button
                type="button"
                style={modal.peligro ? btnDanger : btnPrimary}
                className={modal.peligro ? "habitia-btn-danger" : "habitia-btn-primary"}
                disabled={trabajando}
                onClick={confirmarAccion}
              >
                <modal.Icono /> {modal.boton}
              </button>
            )}
          </div>
        }
      >
        <p style={{ ...S.texto, margin: 0 }}>{modal ? modal.texto : ""}</p>
      </AdminModal>
    </div>
  );
}

/** Título, texto y botón del modal de confirmación según la acción. */
function textoConfirmacion(c, acta) {
  if (c.tipo === "cancelar") {
    return {
      titulo: "Cancelar el procesamiento",
      texto:
        "El audio sale de la cola y la IA no va a redactar el acta. Los audios quedan subidos: los podés volver a procesar cuando se habilite el procesamiento automático. Mientras tanto, el acta se redacta a mano.",
      boton: "Cancelar procesamiento",
      Icono: FaBan,
      peligro: true,
    };
  }
  if (c.tipo === "quitar" && esGrabacionDeSala(c.audio)) {
    return {
      titulo: "Quitar la grabación de la sala",
      texto: `La grabación de Modo Sala de la sesión ${c.audio.sesion || 1} se borra de la asamblea y no se puede volver a sumar. Quitala solo si el audio queda repetido, por ejemplo porque vas a usar la grabadora de respaldo en su lugar.`,
      boton: "Quitar",
      Icono: FaTrash,
      peligro: true,
    };
  }
  if (c.tipo === "salaGrabando") {
    // Procesar con una grabación de Modo Sala todavía abierta (grabando o subiendo lo último).
    const donde = c.dispositivos && c.dispositivos.length ? ` (${c.dispositivos.join(", ")})` : "";
    const situacion =
      c.grabando > 0
        ? `Una grabación de Modo Sala sigue grabando${donde}: si procesás ahora, el acta sale sin lo que falta.`
        : `Una grabación de Modo Sala todavía está subiendo lo último${donde}: si procesás ahora, el acta puede salir sin el final.`;
    return {
      titulo: "Hay una grabación de Modo Sala abierta",
      texto: `${situacion} Lo mejor es terminarla en ese dispositivo y esperar a que diga 100 % subido. Si procesás igual, cuando llegue la podés volver a procesar.`,
      boton: "Procesar igual",
      Icono: FaPlay,
      peligro: false,
    };
  }
  if (c.tipo === "quitar") {
    return {
      titulo: "Quitar el audio",
      texto: "El archivo se borra de la asamblea. Si lo necesitás, lo vas a tener que subir de nuevo.",
      boton: "Quitar",
      Icono: FaTrash,
      peligro: true,
    };
  }
  if (c.tipo === "cancelarMinuta") {
    return {
      titulo: "Cancelar la minuta",
      texto: "La minuta sale de la cola y no se aplica al acta: el acta queda como está. Si después la necesitás, grabala o subila de nuevo desde el editor.",
      boton: "Cancelar la minuta",
      Icono: FaBan,
      peligro: true,
    };
  }
  if (c.tipo === "incluir") {
    const rehace = c.reproceso === true || actaReemplazable(acta);
    let extra = "";
    if (rehace) {
      extra =
        acta && acta.status === "en_revision"
          ? " La IA vuelve a redactar el acta con esa sesión y se pierden las ediciones que hiciste."
          : " La IA vuelve a redactar el acta con esa sesión y reemplaza el borrador.";
    }
    return {
      titulo: "Incluir la sesión",
      texto:
        "La grabación de esta sesión se suma al acta: el audio, la transcripción y la asistencia. Incluila solo si fue parte de la asamblea (por ejemplo, si la reunión se cortó y se volvió a abrir)." +
        extra,
      boton: "Incluir sesión",
      Icono: FaPlus,
      peligro: false,
    };
  }
  if (c.tipo === "sinEsperar") {
    // La sala de Zoom ya cerró, pero Zoom puede seguir procesando la grabación de una sesión.
    let extra = "";
    if (actaReemplazable(acta)) {
      extra =
        acta.status === "en_revision"
          ? " Además, la IA vuelve a redactar el acta y se pierden las ediciones que hiciste."
          : " Además, la IA reemplaza el borrador del acta por su versión.";
    }
    return {
      titulo: "Procesar sin esperar a Zoom",
      texto:
        "Zoom todavía puede estar procesando la grabación de la última sesión: si procesás ahora, esa sesión puede quedar afuera del acta. Cuando llegue, la vas a poder procesar de nuevo." +
        extra,
      boton: "Procesar ahora",
      Icono: FaPlay,
      peligro: false,
    };
  }
  if (c.tipo === "descartar") {
    // Con reproceso: el servidor pidió confirmar que la IA vuelva a redactar el acta existente.
    let extra = "";
    if (c.reproceso === true) {
      extra =
        acta && acta.status === "en_revision"
          ? " La IA vuelve a redactar el acta sin esa sesión y se pierden las ediciones que hiciste."
          : " La IA vuelve a redactar el acta sin esa sesión y reemplaza el borrador.";
    }
    return {
      titulo: "Descartar la sesión",
      texto:
        "La grabación y la asistencia de esta sesión no entran al acta, y no se puede volver a incluir desde acá. Descartala solo si no fue parte de la asamblea (por ejemplo, una prueba o alguien que volvió a abrir la sala cuando ya había terminado): el acta se arma con las otras sesiones." +
        extra,
      boton: "Descartar sesión",
      Icono: FaTimes,
      peligro: true,
    };
  }
  return {
    titulo: "Procesar de nuevo",
    texto:
      acta && acta.status === "borrador"
        ? "Ya hay un borrador del acta. Si procesás el audio, la IA lo reemplaza por su versión."
        : "Ya hay un acta en revisión. Si volvés a procesar, la IA la redacta de nuevo y se pierden las ediciones que hiciste.",
    boton: "Procesar igual",
    Icono: FaCheck,
    peligro: false,
  };
}

function Etapas({ p, apagado = false }) {
  // Con Zoom suma "Buscar la reunión de Zoom", "Traer la grabación" e "Identificar voces".
  const etapas = etapasDe(p);
  const idx = etapas.findIndex((e) => e.id === p.etapa);
  const pendiente = p.estado === "pendiente";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
      <ol style={S.etapas} aria-label="Etapas del procesamiento">
        {etapas.map((e, i) => {
          const hecha = !pendiente && idx > i;
          const actual = !pendiente && idx === i;
          return (
            <li key={e.id} style={S.etapa} aria-current={actual ? "step" : undefined}>
              <span
                style={{
                  ...S.etapaPunto,
                  background: hecha ? colors.success : actual ? colors.primary : colors.gray200,
                  color: hecha || actual ? colors.white : colors.gray600,
                }}
                aria-hidden="true"
              >
                {hecha ? <FaCheck size={9} /> : i + 1}
              </span>
              <span style={{ ...S.texto, color: actual ? colors.primaryDark : hecha ? colors.gray900 : colors.gray600 }}>{e.label}</span>
            </li>
          );
        })}
      </ol>
      {pendiente && apagado ? (
        <p style={{ ...S.hint, color: colors.warningDark }} role="status">
          {AVISO_WORKER_APAGADO}
        </p>
      ) : (
        <p style={S.hint}>
          {pendiente
            ? enColaLocal(p)
              ? "En cola (local): lo toma el worker de tu entorno local."
              : "En cola: arranca en unos minutos."
            : "Procesando: según la duración puede tardar entre 10 y 40 minutos. Podés cerrar esta pantalla; el estado se actualiza solo."}
        </p>
      )}
    </div>
  );
}

function Barra({ pct, etiqueta }) {
  const v = Math.max(0, Math.min(100, Math.round(Number(pct) || 0)));
  return (
    <div style={S.barra} role="progressbar" aria-label={etiqueta} aria-valuemin={0} aria-valuemax={100} aria-valuenow={v}>
      <div style={{ ...S.barraRelleno, width: `${v}%` }} />
    </div>
  );
}

const S = {
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing[3], flexWrap: "wrap" },
  h2: { margin: 0, fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400, display: "flex", alignItems: "center", gap: spacing[2] },
  texto: { margin: 0, fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.5 },
  hint: { margin: 0, fontSize: typography.sizes.sm, color: colors.gray600, lineHeight: 1.5 },
  link: {
    background: "transparent",
    border: 0,
    padding: 0,
    color: colors.primaryDark,
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    transition: `color ${transitions.fast}`,
  },
  actaFila: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[3],
    flexWrap: "wrap",
    background: colors.gray50,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
  },
  error: { background: colors.errorLight, color: colors.errorDark, borderRadius: radius.sm, padding: `${spacing[3]}px ${spacing[4]}px`, fontSize: typography.sizes.md },
  aviso: { background: colors.warningLight, color: colors.warningDark, borderRadius: radius.sm, padding: `${spacing[3]}px ${spacing[4]}px`, fontSize: typography.sizes.md, lineHeight: 1.5 },
  lista: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: spacing[2] },
  audioFila: { display: "flex", alignItems: "center", gap: spacing[3], background: colors.gray50, borderRadius: radius.sm, padding: `${spacing[2]}px ${spacing[3]}px` },
  subida: { display: "flex", flexDirection: "column", gap: spacing[1], background: colors.gray50, borderRadius: radius.sm, padding: `${spacing[2]}px ${spacing[3]}px` },
  acciones: { display: "flex", alignItems: "flex-end", gap: spacing[3], flexWrap: "wrap" },
  etapas: { listStyle: "none", margin: 0, padding: 0, display: "flex", gap: spacing[4], flexWrap: "wrap" },
  etapa: { display: "flex", alignItems: "center", gap: spacing[2] },
  etapaPunto: {
    width: 22,
    height: 22,
    borderRadius: radius.full,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: typography.sizes.xs,
    transition: `background ${transitions.normal}`,
  },
  barra: { height: 8, background: colors.gray100, borderRadius: radius.full, overflow: "hidden" },
  barraRelleno: { height: "100%", background: colors.primary, transition: `width ${transitions.normal}` },
};
